import { withFooter } from '../sms/compose.js';
import { countSegments, type SegmentInfo, toGsmSafe } from '../sms/segments.js';

/**
 * Campaign message templates (M9, owner decisions 2026-10-14 #11–#15). Pure: the editor previews with it,
 * the worker validates at launch and renders every recipient's text at fan-out with the same code.
 *
 *  - Merge fields are `{name|fallback}`. Every one needs a fallback except `{company}` (always known), so
 *    nobody gets a blank or a literal `{…}`. Custom text and dropdown fields use their key.
 *  - The text must name the business: `{company}` or the workspace name (industry rule; counsel list).
 *  - At most one link, `https://` only, never a public link shortener.
 *  - The workspace's opt-out footer goes on its own line under EVERY campaign text (#11); it counts.
 *  - Merged values are made GSM-safe (curly quotes → straight), so a merchant's name never turns a
 *    one-segment text into a costly Unicode one. The template's own characters are the admin's choice,
 *    with the editor's warning.
 */

export const TEMPLATE_MAX_CHARS = 1000;
export const FALLBACK_MAX_CHARS = 40;

/** Built-in merge fields. */
export const BUILT_IN_FIELDS = [
  'first_name',
  'business_name',
  'rep_first_name',
  'company',
] as const;
export type BuiltInField = (typeof BUILT_IN_FIELDS)[number];

export const BUILT_IN_LABELS: Record<BuiltInField, string> = {
  first_name: "Owner's first name",
  business_name: 'Business name',
  rep_first_name: "Rep's first name",
  company: 'Your company name',
};

/** Suggested fallbacks for the editor's "Insert" menu. */
export const DEFAULT_FALLBACKS: Record<Exclude<BuiltInField, 'company'>, string> = {
  first_name: 'there',
  business_name: 'your business',
  rep_first_name: 'your rep',
};

/** Public link shorteners: carriers filter them, and they hide where a link goes (PLAN §12 risk 9). */
export const LINK_SHORTENERS = [
  'bit.ly',
  'bitly.com',
  'tinyurl.com',
  't.co',
  'goo.gl',
  'ow.ly',
  'is.gd',
  'buff.ly',
  'rebrand.ly',
  'cutt.ly',
  'shorturl.at',
  'rb.gy',
  'tiny.cc',
  'bl.ink',
  't.ly',
  's.id',
  'lnkd.in',
  'short.io',
  'shorte.st',
  'v.gd',
  'qr.ae',
  'adf.ly',
  'tr.im',
  'soo.gd',
  'clck.ru',
  'u.to',
  'x.co',
  'zpr.io',
] as const;

export interface MergeTag {
  /** The text as written, braces included. */
  raw: string;
  name: string;
  /** Null when the tag has no `|`. */
  fallback: string | null;
  index: number;
}

const TAG = /\{([^{}|]*)(?:\|([^{}]*))?\}/g;
const NAME = /^[a-z][a-z0-9_]{0,47}$/;

export function parseMergeTags(template: string): MergeTag[] {
  const tags: MergeTag[] = [];
  for (const match of template.matchAll(TAG)) {
    tags.push({
      raw: match[0],
      name: (match[1] ?? '').trim(),
      fallback: match[2] === undefined ? null : match[2],
      index: match.index,
    });
  }
  return tags;
}

/** A field an admin can merge: a built-in, or a custom text/dropdown field by its key. */
export interface MergeableField {
  key: string;
  label: string;
}

export type TemplateIssueCode =
  | 'empty'
  | 'too_long'
  | 'stray_brace'
  | 'unknown_field'
  | 'missing_fallback'
  | 'fallback_too_long'
  | 'no_company'
  | 'too_many_links'
  | 'link_not_https'
  | 'link_shortener';

export interface TemplateIssue {
  code: TemplateIssueCode;
  message: string;
}

export interface FoundLink {
  text: string;
  host: string;
  https: boolean;
}

const TRAILING = /[.,!?;:)\]"'>]+$/;
const BARE_DOMAIN = /^(?:[a-z0-9-]+\.)+[a-z]{2,24}(?::\d+)?(?:[/?#]\S*)?$/i;

/** Links in a text: anything starting http(s):// or www., and bare domains ("acme.com/apply"). */
export function findLinks(text: string): FoundLink[] {
  const links: FoundLink[] = [];
  // Merge tags are not links ("{company}.com" would be odd, but its braces are not part of a host).
  const plain = text.replace(TAG, ' ');
  for (const token of plain.split(/\s+/)) {
    const t = token.replace(TRAILING, '').replace(/^[("'<[]+/, '');
    if (t === '' || t.includes('@')) continue;
    const lower = t.toLowerCase();
    let rest: string | null = null;
    let https = false;
    if (lower.startsWith('https://')) {
      rest = t.slice(8);
      https = true;
    } else if (lower.startsWith('http://')) {
      rest = t.slice(7);
    } else if (lower.startsWith('www.') || BARE_DOMAIN.test(t)) {
      rest = t;
    }
    if (rest === null) continue;
    const host = (rest.split(/[/?#:]/)[0] ?? '').toLowerCase().replace(/^www\./, '');
    if (host === '' || !host.includes('.')) continue;
    links.push({ text: t, host, https });
  }
  return links;
}

function isShortener(host: string): boolean {
  return LINK_SHORTENERS.some((s) => host === s || host.endsWith(`.${s}`));
}

/**
 * Every rule a campaign template must pass before it can be tested, scheduled or launched. `fields` are
 * the merge fields this workspace offers (built-ins plus custom text/dropdown fields).
 */
export function checkTemplate(
  template: string,
  options: { workspaceName: string; customFields: readonly MergeableField[] },
): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  const text = template.trim();
  if (text === '') return [{ code: 'empty', message: 'Write the message' }];
  if (text.length > TEMPLATE_MAX_CHARS) {
    issues.push({ code: 'too_long', message: `Use at most ${TEMPLATE_MAX_CHARS} characters` });
  }
  const outside = text.replace(TAG, '');
  if (outside.includes('{') || outside.includes('}')) {
    issues.push({
      code: 'stray_brace',
      message: 'Remove the stray { or }. Merge fields look like {first_name|there}.',
    });
  }
  const known = new Set<string>([...BUILT_IN_FIELDS, ...options.customFields.map((f) => f.key)]);
  const reported = new Set<string>();
  for (const tag of parseMergeTags(text)) {
    if (!NAME.test(tag.name) || !known.has(tag.name)) {
      if (!reported.has(`u:${tag.raw}`)) {
        reported.add(`u:${tag.raw}`);
        issues.push({
          code: 'unknown_field',
          message: `${tag.raw} isn't a field you can merge. Pick one from Insert field.`,
        });
      }
      continue;
    }
    if (tag.name === 'company') continue;
    const fallback = tag.fallback?.trim() ?? '';
    if (fallback === '') {
      if (!reported.has(`f:${tag.name}`)) {
        reported.add(`f:${tag.name}`);
        issues.push({
          code: 'missing_fallback',
          message: `Add a fallback for {${tag.name}}, like {${tag.name}|${tag.name in DEFAULT_FALLBACKS ? DEFAULT_FALLBACKS[tag.name as keyof typeof DEFAULT_FALLBACKS] : 'something'}}. It's used when a merchant has no value.`,
        });
      }
    } else if (fallback.length > FALLBACK_MAX_CHARS) {
      issues.push({
        code: 'fallback_too_long',
        message: `Keep the fallback for {${tag.name}} under ${FALLBACK_MAX_CHARS} characters`,
      });
    }
  }
  const name = options.workspaceName.trim().toLowerCase();
  const namesCompany =
    parseMergeTags(text).some((t) => t.name === 'company') ||
    (name !== '' && text.toLowerCase().includes(name));
  if (!namesCompany) {
    issues.push({
      code: 'no_company',
      message: `Say who's texting: add {company} or "${options.workspaceName.trim()}".`,
    });
  }
  const links = findLinks(text);
  if (links.length > 1) {
    issues.push({ code: 'too_many_links', message: 'Use at most one link' });
  }
  for (const link of links) {
    if (isShortener(link.host)) {
      issues.push({
        code: 'link_shortener',
        message: `Use the full link instead of ${link.host}. Carriers block link shorteners.`,
      });
    } else if (!link.https) {
      issues.push({
        code: 'link_not_https',
        message: `Write the link in full, starting with https:// (${link.text})`,
      });
    }
  }
  return issues;
}

/** One merchant's values for the merge fields; null or blank uses the tag's fallback. */
export interface MergeValues {
  firstName: string | null;
  businessName: string | null;
  repFirstName: string | null;
  /** Custom fields by key (text and dropdown values). */
  custom: Readonly<Record<string, string | null | undefined>>;
}

/** The first word of a name ("Maria Lopez" → "Maria"). */
export function firstNameOf(name: string | null | undefined): string | null {
  const first = (name ?? '').trim().split(/\s+/)[0] ?? '';
  return first === '' ? null : first;
}

function cleanValue(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const t = toGsmSafe(value.replace(/\s+/g, ' ').trim()).text;
  return t === '' ? null : t;
}

/** The template with this merchant's values. Call only with a template that passed checkTemplate. */
export function renderTemplate(template: string, values: MergeValues, companyName: string): string {
  return template
    .trim()
    .replace(TAG, (_raw, rawName: string, fallback: string | undefined) => {
      const name = rawName.trim();
      const pick = (v: string | null | undefined) => cleanValue(v) ?? (fallback ?? '').trim();
      switch (name) {
        case 'company':
          return cleanValue(companyName) ?? '';
        case 'first_name':
          return pick(values.firstName);
        case 'business_name':
          return pick(values.businessName);
        case 'rep_first_name':
          return pick(values.repFirstName);
        default:
          return pick(values.custom[name]);
      }
    })
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

export interface RenderedCampaignText {
  /** What the merchant receives: the rendered text, then the opt-out footer on its own line. */
  text: string;
  info: SegmentInfo;
}

/** The final text of one recipient, footer included, with its segment count (shared counter). */
export function campaignText(
  template: string,
  values: MergeValues,
  companyName: string,
  footer: string,
): RenderedCampaignText {
  const text = withFooter(renderTemplate(template, values, companyName), footer);
  return { text, info: countSegments(text) };
}

/** The text a merchant with no values at all would get (every fallback): the editor's sample. */
export function sampleText(template: string, companyName: string, footer: string) {
  return campaignText(
    template,
    { firstName: null, businessName: null, repFirstName: null, custom: {} },
    companyName,
    footer,
  );
}
